import { apiRequest } from '@/utils/api-request';
import { CustomNode } from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import * as Sentry from '@sentry/react';

export interface RerunFromNodeRequest {
  /**
   * session_id of the existing past run whose cached node_result
   * rows seed the new run.
   */
  sourceSessionId: string;
  /**
   * react_flow_generated_ids of nodes the user picked as the
   * starting point.  These nodes plus everything downstream of them
   * execute fresh; every other completed node from the source run
   * is seeded into the new run as ``cached``.
   */
  startingNodeIds: string[];
  /**
   * AdvancedWorkflowPayload for the new run.  Typically equal to
   * the source run's payload (rerun without edits) but may include
   * edits applied since.  ``payload.session_id`` becomes the new
   * run's identifier; it must differ from sourceSessionId.
   */
  payload: {
    id: string;
    session_id: string;
    name: string;
    nodes: CustomNode[];
    edges: CustomEdge[];
    [k: string]: unknown;
  };
}

/**
 * Start a brand-new workflow_run that inherits cached node results
 * from a previous run.
 *
 * The backend:
 * 1. Creates a new ``workflow_run`` with ``parent_run_id`` pointing
 *    at the source run.  WebSocket events flow on the new run's
 *    session_id; the old run is unaffected.
 * 2. Pre-seeds the workflow's in-memory results dict with the
 *    source run's ``node_result`` rows EXCEPT the
 *    ``startingNodeIds`` set and everything downstream of them.
 *    The seeded rows are ALSO persisted against the new run so the
 *    run-detail view shows them with a cached_from_run_id pointer
 *    to the source.
 * 3. Dispatches the starting nodes + downstream as fresh
 *    activities; seeded nodes are skipped during dispatch.
 *
 * Returns the same envelope as run-workflow so the caller can
 * connect the WebSocket and update store state via the same code
 * path as a normal run.
 *
 * Only available when the backend has USE_TEMPORAL=true; the legacy
 * celery executor returns 400.
 */
export const rerunFromNode = async (
  workflowId: string,
  orgId: string,
  request: RerunFromNodeRequest,
): Promise<RerunFromNodeResponse> => {
  if (request.payload.session_id === request.sourceSessionId) {
    throw new Error(
      'rerunFromNode: payload.session_id must differ from sourceSessionId',
    );
  }
  if (request.startingNodeIds.length === 0) {
    throw new Error('rerunFromNode: startingNodeIds must not be empty');
  }

  try {
    // The HTTP envelope is whatever the run-workflow endpoint returns
    // (success, message, session_id, optional detail) -- we type it
    // loosely here for the same reason runWorkflow does: apiRequest
    // returns ApiResponse<T> and the existing call sites dereference
    // the inner shape directly.
    const response = await apiRequest<RerunFromNodeResponse>({
      url: `/workflow/${orgId}/${workflowId}/rerun-from-node`,
      method: 'POST',
      target: 'workflow',
      body: {
        source_session_id: request.sourceSessionId,
        starting_node_ids: request.startingNodeIds,
        payload: request.payload,
      },
    });
    return response as unknown as RerunFromNodeResponse;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'rerun-from-node',
        workflowId,
        sourceSessionId: request.sourceSessionId,
        startingNodeCount: request.startingNodeIds.length,
      },
    });
    throw error;
  }
};

/**
 * Response envelope from /rerun-from-node.  Matches the
 * /async-run-workflow envelope -- 202 Accepted with a job_id the
 * frontend can use to correlate the run.
 */
export interface RerunFromNodeResponse {
  success: boolean;
  message: string;
  session_id: string;
  workflow_id?: string;
  job_id?: string;
  detail?: string;
}
